import i18n from '@/i18n';
import {
  addDownloadCenterRecord,
  patchDownloadCenterRecord,
  queueBrowserBlobDownload,
} from '@/utils/downloadCenter';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function sanitizeFilePart(name) {
  return String(name || '').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'file';
}

function ensureXlsxName(name, index = 0) {
  const base = sanitizeFilePart(name || `上架表格_${index + 1}`);
  return /\.xlsx$/i.test(base) ? base : `${base}.xlsx`;
}

function toExcelBlob(buffer) {
  if (buffer instanceof Blob) return buffer;
  return new Blob([buffer], { type: EXCEL_MIME });
}

/**
 * 将按模板导出的 Excel 直接写入下载中心（不打包 zip）。
 * 多模板时每个表格一条记录。
 * @returns {Promise<string[]>} 下载中心记录 id 列表
 */
export async function queueListingTemplateExcelToDownloadCenter(
  excelFiles,
  {
    source = '按模板导出',
    recordId = '',
  } = {},
) {
  const files = (Array.isArray(excelFiles) ? excelFiles : []).filter((f) => f?.buffer != null);
  if (files.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.noExportTableFile'));
  }

  const placeholderId = String(recordId || '').trim();
  const usedNames = new Set();
  const ids = [];

  try {
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      let fileName = ensureXlsxName(file?.fileName, index);
      if (usedNames.has(fileName.toLowerCase())) {
        const stem = fileName.replace(/\.xlsx$/i, '');
        fileName = `${stem}_${index + 1}.xlsx`;
      }
      usedNames.add(fileName.toLowerCase());

      const id = index === 0 && placeholderId
        ? placeholderId
        : `dl-${Date.now()}-listing-${index}-${Math.random().toString(36).slice(2, 8)}`;

      if (index === 0 && placeholderId) {
        patchDownloadCenterRecord(id, {
          fileName,
          source,
          status: 'downloading',
          error: null,
          progress: 60,
          itemCount: 1,
        });
      } else if (!placeholderId && index === 0) {
        addDownloadCenterRecord({
          id,
          fileName,
          url: '',
          source,
          status: 'downloading',
          createdAt: new Date().toISOString(),
          completedAt: null,
          downloadedAt: null,
          error: null,
          progress: 60,
          itemCount: 1,
        });
      }

      await queueBrowserBlobDownload({
        blob: toExcelBlob(file.buffer),
        fileName,
        source,
        recordId: id,
        meta: { itemCount: 1 },
      });
      ids.push(id);
    }
    return ids;
  } catch (error) {
    if (placeholderId) {
      patchDownloadCenterRecord(placeholderId, {
        status: 'failed',
        error: String(error?.message || error),
        completedAt: new Date().toISOString(),
      });
    }
    throw error;
  }
}
